import AutoclaveItem from "./Autoclave"
import { Autoclave, AutoclaveType, Frequency, OpenAutoclaveModal, OpenAssignBoxModal, SporetestingResult, OpenSporetestModal, SporetestLog } from "../types"
import { Biobox, TeamMember } from "../../../../types"
import { Dispatch, SetStateAction, useEffect, useMemo, useRef, useState } from "react"
import { AutoclaveTypeId } from "../enums"
import { CustomerState } from "../../../../redux/customer"
import { ChildCustomerState } from "../../../../redux/childCustomer"

type AutoclavesInfiniteScroll = {
  autoclaves: Autoclave[],
  customer: CustomerState | ChildCustomerState,
  setAutoclaves: Dispatch<SetStateAction<Autoclave[]>>,
  setSporetests: Dispatch<SetStateAction<SporetestingResult[]>>,
  setOpenAssignBioboxModal: Dispatch<SetStateAction<OpenAssignBoxModal>>,
  setOpenAutoclaveModal: Dispatch<SetStateAction<OpenAutoclaveModal>>,
  setOpenSporetestModal: Dispatch<SetStateAction<OpenSporetestModal>>,
  autoclaveTypeById: Map<AutoclaveTypeId, AutoclaveType>,
  autoclaveFrequencyById: Map<number, Frequency>,
  bioboxesById: Map<number, Biobox>,
  sporetestsById: Map<number, SporetestingResult>,
  sporetestsByAutoclaveId: Map<number, SporetestingResult[]>,
  sporetestLogsByAutoclaveId: Map<number, SporetestLog[]>,
  usersById: Map<number, TeamMember>,
}

const AutoclavesInfiniteScroll = (props: AutoclavesInfiniteScroll) => {
  const {
    autoclaves,
    customer,
    setAutoclaves,
    setSporetests,
    setOpenAssignBioboxModal,
    setOpenAutoclaveModal,
    setOpenSporetestModal,
    autoclaveTypeById,
    autoclaveFrequencyById,
    bioboxesById,
    sporetestsById,
    sporetestsByAutoclaveId,
    sporetestLogsByAutoclaveId,
    usersById,
  } = props

  const [page, setPage] = useState(0)

  const observerTarget = useRef(null)

  const bioboxes = Array.from(bioboxesById.values())

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setPage(prevPage => prevPage + 1)
        }
      },
      { threshold: 1 }
    )

    if (observerTarget.current) {
      observer.observe(observerTarget.current)
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current)
      }
    }
  }, [observerTarget])

  const autoclaveSubset = useMemo(() => {
    const pageSize = 10
    const start = 0
    const end = (page + 1) * pageSize
    
    const sortedAutoclaves = [...autoclaves].sort((a, b) => {
      const results = {
        a: sporetestsByAutoclaveId.get(Number(a.autoclave_id)) ?? [],
        b: sporetestsByAutoclaveId.get(Number(b.autoclave_id)) ?? [],
      }

      const aActiveWithResults = a.active && results.a.length > 0
      const bActiveWithResults = b.active && results.b.length > 0
      
      const aActiveWithoutResults = a.active && results.a.length === 0
      const bActiveWithoutResults = b.active && results.b.length === 0
      
      const aInactive = !a.active
      const bInactive = !b.active
      
      if (aActiveWithResults && !bActiveWithResults) return -1
      if (!aActiveWithResults && bActiveWithResults) return 1
      if (aActiveWithoutResults && !bActiveWithoutResults && !bActiveWithResults) return -1
      if (!aActiveWithoutResults && bActiveWithoutResults && !aActiveWithResults) return 1
      if (aInactive && !bInactive) return 1
      if (!aInactive && bInactive) return -1
      
      return 0
    })
    
    const autoclaveSubset = sortedAutoclaves.slice(start, end)

    return autoclaveSubset
  }, [autoclaves, bioboxes, page])

  return (
    <>
      {
        autoclaveSubset.map(autoclave => (
          <AutoclaveItem
            key={autoclave.autoclave_id}
            autoclave={autoclave}
            autoclaves={autoclaves}
            customer={customer}
            setAutoclaves={setAutoclaves}
            setSporetests={setSporetests}
            setOpenAssignBioboxModal={setOpenAssignBioboxModal}
            setOpenAutoclaveModal={setOpenAutoclaveModal}
            setOpenSporetestModal={setOpenSporetestModal}
            autoclaveTypeById={autoclaveTypeById}
            autoclaveFrequencyById={autoclaveFrequencyById}
            bioboxesById={bioboxesById}
            sporetestsById={sporetestsById}
            sporetestsByAutoclaveId={sporetestsByAutoclaveId}
            sporetestLogsByAutoclaveId={sporetestLogsByAutoclaveId}
            usersById={usersById}
          />
        ))
      }
      {
        autoclaveSubset.length < autoclaves.length && (
          <div ref={observerTarget}></div>
        )
      }
    </>
  )
}

export default AutoclavesInfiniteScroll
